import { useState, useCallback, useRef, useEffect, startTransition } from 'react';
import type {
  PageBuilderFormData,
  PageBuilderHeader,
  PageBuilderMetadata,
  ContentBlock,
  BlockType,
} from '@/lib/types/page-builder';
import type { UserSearchResult } from '@/lib/types/team';
import { createBlock, createEmptyFormData } from '@/lib/types/page-builder';
import { pageBuilderFormSchema, validateAllBlocks } from '@/lib/validations/page-builder';

// ============================================================================
// TYPES
// ============================================================================

interface UsePageBuilderOptions {
  initialData?: Partial<PageBuilderFormData>;
  onAutoSave?: (data: PageBuilderFormData) => void;
  autoSaveInterval?: number;
}

interface UsePageBuilderReturn {
  // Form data
  formData: PageBuilderFormData;

  // Header operations
  updateHeader: <K extends keyof PageBuilderHeader>(
    field: K,
    value: PageBuilderHeader[K]
  ) => void;

  // Block operations
  addBlock: (type: BlockType, afterId?: string) => string;
  updateBlock: (id: string, updates: Partial<ContentBlock>) => void;
  updateBlockWith: (
    id: string,
    updater: (block: ContentBlock) => Partial<ContentBlock>
  ) => void;
  deleteBlock: (id: string) => void;
  moveBlock: (fromIndex: number, toIndex: number) => void;
  reorderBlocks: (newBlocks: ContentBlock[]) => void;
  duplicateBlock: (id: string) => string | null;
  getBlockById: (id: string) => ContentBlock | undefined;
  getBlockIndex: (id: string) => number;

  // Metadata operations
  updateMetadata: <K extends keyof PageBuilderMetadata>(
    field: K,
    value: PageBuilderMetadata[K]
  ) => void;
  updateMetadataWith: <K extends keyof PageBuilderMetadata>(
    field: K,
    updater: (value: PageBuilderMetadata[K]) => PageBuilderMetadata[K]
  ) => void;

  // Pending invitation operations
  addPendingInvitation: (user: UserSearchResult, role: string) => void;
  removePendingInvitation: (userId: number) => void;
  updatePendingInvitationRole: (userId: number, role: string) => void;

  // Undo/Redo
  undo: () => void;
  redo: () => void;
  canUndo: boolean;
  canRedo: boolean;

  // State
  isDirty: boolean;
  isSaving: boolean;
  errors: Map<string, string[]>;

  // Validation
  validate: () => boolean;
  validateBlocks: () => Map<string, string[]>;

  // Actions
  reset: () => void;
  setFormData: (data: PageBuilderFormData) => void;
}

// ============================================================================
// HOOK
// ============================================================================

const MAX_HISTORY_LENGTH = 50;

export function usePageBuilder(options: UsePageBuilderOptions = {}): UsePageBuilderReturn {
  const { initialData, onAutoSave, autoSaveInterval = 30000 } = options;

  // Main state
  const [formData, setFormDataInternal] = useState<PageBuilderFormData>(() => {
    const empty = createEmptyFormData();
    if (initialData) {
      return {
        header: { ...empty.header, ...initialData.header },
        blocks: initialData.blocks ?? empty.blocks,
        metadata: { ...empty.metadata, ...initialData.metadata },
      };
    }
    return empty;
  });

  // History for undo/redo
  const [history, setHistory] = useState<PageBuilderFormData[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);

  // State flags
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving] = useState(false);
  const [errors, setErrors] = useState<Map<string, string[]>>(new Map());

  // Refs for auto-save
  const lastSavedRef = useRef<string>('');
  const onAutoSaveRef = useRef(onAutoSave);
  onAutoSaveRef.current = onAutoSave;

  // ============================================================================
  // HISTORY MANAGEMENT
  // ============================================================================

  const pushToHistory = useCallback((newData: PageBuilderFormData) => {
    setHistory((prev) => {
      // If we're not at the end of history, truncate future states
      const truncated = prev.slice(0, historyIndex + 1);
      const newHistory = [...truncated, newData];

      // Limit history length
      if (newHistory.length > MAX_HISTORY_LENGTH) {
        return newHistory.slice(-MAX_HISTORY_LENGTH);
      }
      return newHistory;
    });
    setHistoryIndex((prev) => Math.min(prev + 1, MAX_HISTORY_LENGTH - 1));
  }, [historyIndex]);

  const canUndo = historyIndex > 0;
  const canRedo = historyIndex < history.length - 1;

  const undo = useCallback(() => {
    if (!canUndo) return;
    const newIndex = historyIndex - 1;
    setHistoryIndex(newIndex);
    setFormDataInternal(history[newIndex]);
    setIsDirty(true);
  }, [canUndo, historyIndex, history]);

  const redo = useCallback(() => {
    if (!canRedo) return;
    const newIndex = historyIndex + 1;
    setHistoryIndex(newIndex);
    setFormDataInternal(history[newIndex]);
    setIsDirty(true);
  }, [canRedo, historyIndex, history]);

  // ============================================================================
  // FORM DATA OPERATIONS
  // ============================================================================

  const updateFormData = useCallback((
    updater: (prev: PageBuilderFormData) => PageBuilderFormData,
    addToHistory = true
  ) => {
    setFormDataInternal((prev) => {
      const newData = updater(prev);
      if (addToHistory) {
        pushToHistory(newData);
      }
      return newData;
    });
    setIsDirty(true);
  }, [pushToHistory]);

  const setFormData = useCallback((data: PageBuilderFormData) => {
    setFormDataInternal(data);
    pushToHistory(data);
    setIsDirty(true);
  }, [pushToHistory]);

  const reset = useCallback(() => {
    const empty = createEmptyFormData();
    setFormDataInternal(empty);
    setHistory([empty]);
    setHistoryIndex(0);
    setIsDirty(false);
    setErrors(new Map());
  }, []);

  // ============================================================================
  // HEADER OPERATIONS
  // ============================================================================

  const updateHeader = useCallback(<K extends keyof PageBuilderHeader>(
    field: K,
    value: PageBuilderHeader[K]
  ) => {
    updateFormData((prev) => ({
      ...prev,
      header: { ...prev.header, [field]: value },
    }));
  }, [updateFormData]);

  // ============================================================================
  // BLOCK OPERATIONS
  // ============================================================================

  const addBlock = useCallback((type: BlockType, afterId?: string): string => {
    const newBlock = createBlock(type);

    updateFormData((prev) => {
      if (!afterId) {
        return { ...prev, blocks: [...prev.blocks, newBlock] };
      }

      const index = prev.blocks.findIndex((b) => b.id === afterId);
      if (index === -1) {
        return { ...prev, blocks: [...prev.blocks, newBlock] };
      }

      const newBlocks = [...prev.blocks];
      newBlocks.splice(index + 1, 0, newBlock);
      return { ...prev, blocks: newBlocks };
    });

    return newBlock.id;
  }, [updateFormData]);

  const updateBlock = useCallback((id: string, updates: Partial<ContentBlock>) => {
    updateFormData((prev) => ({
      ...prev,
      blocks: prev.blocks.map((block) =>
        block.id === id
          ? { ...block, ...updates, updatedAt: Date.now() }
          : block
      ) as ContentBlock[],
    }));
  }, [updateFormData]);

  // Functional variant: the updater receives the CURRENT block, so async
  // callers (e.g. background image uploads finishing out of order) can't
  // clobber each other with stale content snapshots.
  const updateBlockWith = useCallback((
    id: string,
    updater: (block: ContentBlock) => Partial<ContentBlock>
  ) => {
    updateFormData((prev) => ({
      ...prev,
      blocks: prev.blocks.map((block) =>
        block.id === id
          ? ({ ...block, ...updater(block), updatedAt: Date.now() } as ContentBlock)
          : block
      ),
    }));
  }, [updateFormData]);

  const deleteBlock = useCallback((id: string) => {
    updateFormData((prev) => ({
      ...prev,
      blocks: prev.blocks.filter((block) => block.id !== id),
    }));
  }, [updateFormData]);

  const moveBlock = useCallback((fromIndex: number, toIndex: number) => {
    if (fromIndex === toIndex) return;

    updateFormData((prev) => {
      const newBlocks = [...prev.blocks];
      const [movedBlock] = newBlocks.splice(fromIndex, 1);
      newBlocks.splice(toIndex, 0, movedBlock);
      return { ...prev, blocks: newBlocks };
    });
  }, [updateFormData]);

  const reorderBlocks = useCallback((newBlocks: ContentBlock[]) => {
    updateFormData((prev) => ({ ...prev, blocks: newBlocks }));
  }, [updateFormData]);

  const duplicateBlock = useCallback((id: string): string | null => {
    const blockIndex = formData.blocks.findIndex((b) => b.id === id);
    if (blockIndex === -1) return null;

    const originalBlock = formData.blocks[blockIndex];
    const newBlock: ContentBlock = {
      ...originalBlock,
      id: `block_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
      createdAt: Date.now(),
      updatedAt: Date.now(),
    } as ContentBlock;

    updateFormData((prev) => {
      const newBlocks = [...prev.blocks];
      newBlocks.splice(blockIndex + 1, 0, newBlock);
      return { ...prev, blocks: newBlocks };
    });

    return newBlock.id;
  }, [formData.blocks, updateFormData]);

  const getBlockById = useCallback((id: string) => {
    return formData.blocks.find((b) => b.id === id);
  }, [formData.blocks]);

  const getBlockIndex = useCallback((id: string) => {
    return formData.blocks.findIndex((b) => b.id === id);
  }, [formData.blocks]);

  // ============================================================================
  // METADATA OPERATIONS
  // ============================================================================

  const updateMetadata = useCallback(<K extends keyof PageBuilderMetadata>(
    field: K,
    value: PageBuilderMetadata[K]
  ) => {
    updateFormData((prev) => ({
      ...prev,
      metadata: { ...prev.metadata, [field]: value },
    }));
  }, [updateFormData]);

  // Functional variant — same rationale as updateBlockWith
  const updateMetadataWith = useCallback(<K extends keyof PageBuilderMetadata>(
    field: K,
    updater: (value: PageBuilderMetadata[K]) => PageBuilderMetadata[K]
  ) => {
    updateFormData((prev) => ({
      ...prev,
      metadata: { ...prev.metadata, [field]: updater(prev.metadata[field]) },
    }));
  }, [updateFormData]);

  // ============================================================================
  // PENDING INVITATION OPERATIONS
  // ============================================================================

  const addPendingInvitation = useCallback((user: UserSearchResult, role: string) => {
    updateFormData((prev) => ({
      ...prev,
      metadata: {
        ...prev.metadata,
        pendingInvitations: [...(prev.metadata.pendingInvitations || []), { user, role }],
      },
    }));
  }, [updateFormData]);

  const removePendingInvitation = useCallback((userId: number) => {
    updateFormData((prev) => ({
      ...prev,
      metadata: {
        ...prev.metadata,
        pendingInvitations: (prev.metadata.pendingInvitations || []).filter(
          (inv) => inv.user.id !== userId
        ),
      },
    }));
  }, [updateFormData]);

  const updatePendingInvitationRole = useCallback((userId: number, role: string) => {
    updateFormData((prev) => ({
      ...prev,
      metadata: {
        ...prev.metadata,
        pendingInvitations: (prev.metadata.pendingInvitations || []).map((inv) =>
          inv.user.id === userId ? { ...inv, role } : inv
        ),
      },
    }));
  }, [updateFormData]);

  // ============================================================================
  // VALIDATION
  // ============================================================================

  const validateBlocks = useCallback(() => {
    return validateAllBlocks(formData.blocks);
  }, [formData.blocks]);

  const validate = useCallback(() => {
    // Validate with Zod
    const result = pageBuilderFormSchema.safeParse(formData);

    if (!result.success) {
      // Convert Zod errors to our error map
      const newErrors = new Map<string, string[]>();

      result.error.issues.forEach((issue) => {
        const path = issue.path.join('.');
        const existing = newErrors.get(path) ?? [];
        newErrors.set(path, [...existing, issue.message]);
      });

      setErrors(newErrors);
      return false;
    }

    // Also validate individual blocks
    const blockErrors = validateBlocks();
    if (blockErrors.size > 0) {
      setErrors(blockErrors);
      return false;
    }

    setErrors(new Map());
    return true;
  }, [formData, validateBlocks]);

  // ============================================================================
  // AUTO-SAVE (silent — no state updates during save to avoid re-renders)
  // ============================================================================

  const formDataRef = useRef(formData);
  formDataRef.current = formData;

  const isFirstRenderRef = useRef(true);

  useEffect(() => {
    // On first render, just record initial data hash
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      lastSavedRef.current = JSON.stringify(formData);
      return;
    }

    // Debounced save — writes to localStorage silently, then defers indicator update
    const timer = setTimeout(() => {
      const data = formDataRef.current;
      const serialized = JSON.stringify(data);

      if (serialized !== lastSavedRef.current) {
        onAutoSaveRef.current?.(data);
        lastSavedRef.current = serialized;
      }

      // Defer indicator update so it never interrupts user input
      startTransition(() => {
        setIsDirty(false);
      });
    }, autoSaveInterval);

    return () => clearTimeout(timer);
  }, [formData, autoSaveInterval]);

  // Initialize history with initial data
  useEffect(() => {
    if (history.length === 0) {
      setHistory([formData]);
      setHistoryIndex(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ============================================================================
  // RETURN
  // ============================================================================

  return {
    formData,
    updateHeader,
    addBlock,
    updateBlock,
    updateBlockWith,
    deleteBlock,
    moveBlock,
    reorderBlocks,
    duplicateBlock,
    getBlockById,
    getBlockIndex,
    updateMetadata,
    updateMetadataWith,
    addPendingInvitation,
    removePendingInvitation,
    updatePendingInvitationRole,
    undo,
    redo,
    canUndo,
    canRedo,
    isDirty,
    isSaving,
    errors,
    validate,
    validateBlocks,
    reset,
    setFormData,
  };
}
